import { useCallback, useEffect, useRef, useState } from 'react';

import { colors } from '@EVOCORP/corechat-theme';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Menu,
  Paper,
  Tooltip,
  Zoom,
} from '@material-ui/core';
import { blue, deepOrange, deepPurple, purple } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { CloseOutlined } from '@material-ui/icons';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PersonIcon from '@material-ui/icons/Person';
import PhotoIcon from '@material-ui/icons/Photo';
import PhotoFilterIcon from '@material-ui/icons/PhotoFilter';
import SendIcon from '@material-ui/icons/Send';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'react-use';

import { ReactComponent as InteractiveIcon } from 'assets/images/add-interactive-message.svg';
import ActionButton from 'components/ActionButton';
import AudioRecorder from 'components/AudioRecorder';
import ButtonEncaminharMensagens from 'components/ButtonEncaminharMensagens';
import { ChatInput, ChatInputRef } from 'components/ChatInput';
import { MentionItem } from 'components/ChatInput/mentionSuggestion';
import EmojiButton from 'components/EmojiButton';
import HiddenFileInput from 'components/HiddenFileInput';
import ModalEnviarMensagemCTA from 'components/ModalEnviarMensagemCTA';
import ModalGaleria from 'components/ModalGaleria';
import ModalListaContatos from 'components/ModalListaContatos';
import ModalRespostasProntas from 'components/ModalRespostasProntas';
import ReplyMessagePreview from 'components/ReplyMessagePreview';
import {
  LOCAL_STORAGE_KEY_INPUT_GRUPO,
  LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO,
} from 'constants/localStorageKeys';
import { useAppDispatch, useAppSelector } from 'store';
import { atendimentosSelector } from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import { ContatoInterface } from 'store/reducers/contatos';
import {
  fetchContatosParaMencao,
  resetContatosGrupo,
} from 'store/reducers/grupos';
import {
  mensagensSelector,
  resetMensagensParaEncaminhar,
  resetReplyMessage,
} from 'store/reducers/mensagens';
import { ContatoMencionado } from 'types';
import { incluirMencaoTodos } from 'utils/mencaoTodos';
import { whatsappToHtml } from 'utils/whatsappToHtml';

export interface InputFormMessageWithMentionsProps {
  maxChars?: number;
  grupoChatId: string;
  onSendInputText?: (
    texto: string,
    contatosMencionados: ContatoMencionado[],
    callback: (isSuccess: boolean) => void,
  ) => void;
  onSendContato?: (contato: ContatoInterface) => void;
  onSendAudio?: (mediaBlob: Blob, duration: number) => void;
  onSendFromGaleria?: (storageIds: string[]) => void;
  onSendCTA?: () => void;
  onSelectFiles?: (files: File[]) => void;
}

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    padding: 0,
    marginTop: spacing(1),
    marginBottom: spacing(1),
  },
  rootInput: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    color: palette.text.secondary,
    '& > *': {
      margin: spacing(0, 1),
    },
  },
  inputContainer: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 2),
    borderRadius: spacing(3),
    backgroundColor: palette.background.default,
    cursor: 'text',
    '& > *:not(:first-child)': {
      marginLeft: spacing(1.5),
    },
  },
  paper: {
    marginTop: -spacing(3),
    background: 'transparent',
  },
  menuContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(1),
    background: 'transparent',
    '& > *': {
      margin: spacing(1, 0),
    },
  },
}));

export default function InputFormMessageWithMentions(
  props: InputFormMessageWithMentionsProps,
) {
  const {
    maxChars = 6000,
    grupoChatId,
    onSendInputText,
    onSendContato,
    onSendAudio,
    onSendFromGaleria,
    onSendCTA,
    onSelectFiles,
  } = props;

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [inputValue, setInputValue] = useState(() => {
    const shouldRestore =
      localStorage.getItem('restore_caption_flag') === 'true';
    if (shouldRestore) {
      return localStorage.getItem(LOCAL_STORAGE_KEY_INPUT_GRUPO) || '';
    }
    return '';
  });
  const [inputMentions, setInputMentions] = useState<ContatoMencionado[]>(
    () => {
      const shouldRestore =
        localStorage.getItem('restore_caption_flag') === 'true';
      if (shouldRestore) {
        const saved = localStorage.getItem(
          LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO,
        );
        return saved ? JSON.parse(saved) : [];
      }
      return [];
    },
  );
  const [hasContent, setHasContent] = useState(() => {
    const shouldRestore =
      localStorage.getItem('restore_caption_flag') === 'true';
    if (shouldRestore) {
      return !!(
        localStorage.getItem(LOCAL_STORAGE_KEY_INPUT_GRUPO) || ''
      ).trim();
    }
    return false;
  });

  const DEBOUNCE_SAVE_DELAY_MS = 300;
  const [, cancelDebounce] = useDebounce(
    () => {
      if (inputValue.trim()) {
        localStorage.setItem(LOCAL_STORAGE_KEY_INPUT_GRUPO, inputValue);
        if (inputMentions.length > 0) {
          localStorage.setItem(
            LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO,
            JSON.stringify(inputMentions),
          );
        } else {
          localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO);
        }
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_GRUPO);
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO);
      }
    },
    DEBOUNCE_SAVE_DELAY_MS,
    [inputValue, inputMentions],
  );

  const chatInputRef = useRef<ChatInputRef>(null);

  const getInitialValue = useCallback(() => {
    const shouldRestore =
      localStorage.getItem('restore_caption_flag') === 'true';
    if (!shouldRestore) return '';

    const initialValue =
      localStorage.getItem(LOCAL_STORAGE_KEY_INPUT_GRUPO) || '';
    if (!initialValue) return '';
    let html = whatsappToHtml(initialValue);

    const savedMentions = localStorage.getItem(
      LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO,
    );
    const initialMentions: ContatoMencionado[] = savedMentions
      ? JSON.parse(savedMentions)
      : [];

    if (initialMentions?.length) {
      initialMentions.forEach(contato => {
        const alias = `@${contato.contatoNome || contato.waPhoneNumberId}`;
        const regex = new RegExp(`@${contato.waPhoneNumberId}`, 'g');
        const span = `<span data-type="mention" data-id="${
          contato.contatoId
        }" data-label="${
          contato.contatoNome || contato.waPhoneNumberId
        }" data-wa-phone-number-id="${contato.waPhoneNumberId}" data-contact="${
          contato.waPhoneNumberId
        }">${alias}</span>`;
        html = html.replace(regex, span);
      });
    }
    return html;
  }, []);

  const dispatch = useAppDispatch();
  const reduxDispatch = useDispatch();

  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);
  const {
    isEncaminhandoMensagens,
    totalMensagensParaEncaminhar,
    replyMessage,
  } = useAppSelector(mensagensSelector);

  const tipoCanal = atendimentoSelecionado?.canal?.tipoCanal;
  const isOfficialWhatsAppChannel =
    tipoCanal === TipoCanalEnum.WHATSAPP_OFICIAL;
  const isInstagramOrMessengerChannel =
    tipoCanal === TipoCanalEnum.INSTAGRAM ||
    tipoCanal === TipoCanalEnum.MESSENGER;

  const prevGrupoChatIdRef = useRef<string | undefined>();

  useEffect(() => {
    if (grupoChatId) {
      dispatch(resetContatosGrupo());
    }

    if (
      prevGrupoChatIdRef.current !== undefined &&
      prevGrupoChatIdRef.current !== grupoChatId
    ) {
      cancelDebounce();
      chatInputRef.current?.clear();
      setInputValue('');
      setInputMentions([]);
      setHasContent(false);
      localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_GRUPO);
      localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO);
    }

    prevGrupoChatIdRef.current = grupoChatId;
  }, [dispatch, grupoChatId, cancelDebounce]);

  useEffect(() => {
    localStorage.removeItem('restore_caption_flag');
  }, []);

  const fetchUsers = useCallback(
    async (query: string): Promise<MentionItem[]> => {
      if (!grupoChatId) return [];

      try {
        const contatos = await dispatch(
          fetchContatosParaMencao({ grupoChatId, filter: query }),
        ).unwrap();
        return incluirMencaoTodos(
          query,
          contatos.map(c => ({
            id: c.id,
            label: c.nome,
            waPhoneNumberId: c.waPhoneNumberId ?? '',
            contact: c.contato,
            foto: c.foto,
          })),
        );
      } catch (error) {
        enqueueSnackbar(
          'Erro ao buscar contatos para menção. Tente novamente.',
        );
        return [];
      }
    },
    [dispatch, enqueueSnackbar, grupoChatId],
  );

  const handleSendInputText = useCallback(
    (text: string, mentions: ContatoMencionado[]) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (trimmed.length > maxChars) {
        enqueueSnackbar(
          `Você não pode enviar mais de ${maxChars} caracteres de uma vez.`,
        );
        return;
      }

      if (onSendInputText) {
        setLoading(true);
        cancelDebounce();
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_GRUPO);
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO);
        onSendInputText(trimmed, mentions, (isSuccess: boolean) => {
          setLoading(false);
          if (isSuccess) {
            chatInputRef.current?.clear();
            setInputValue('');
            setInputMentions([]);
            setHasContent(false);
          }
          chatInputRef.current?.focus();
        });
      }
    },
    [enqueueSnackbar, maxChars, onSendInputText, cancelDebounce],
  );

  const handleSelectEmoji = (emoji: string) => {
    chatInputRef.current?.insertContent(emoji);
  };

  const handleSelectRespostaPronta = (texto: string) => {
    chatInputRef.current?.setContent(texto);
    chatInputRef.current?.focus();
  };

  const handleClearEditor = () => {
    cancelDebounce();
    chatInputRef.current?.clear();
    localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_GRUPO);
    localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO);
    setInputValue('');
    setInputMentions([]);
    setHasContent(false);
  };

  const handleClearReplyMessage = useCallback(() => {
    reduxDispatch(resetReplyMessage());
  }, [reduxDispatch]);

  const handleCancelEncaminharMensagens = useCallback(() => {
    reduxDispatch(resetMensagensParaEncaminhar());
  }, [reduxDispatch]);

  useEffect(() => {
    if (replyMessage) chatInputRef.current?.focus();
  }, [replyMessage]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isEncaminhandoMensagens) {
        handleCancelEncaminharMensagens();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleCancelEncaminharMensagens, isEncaminhandoMensagens]);

  const [showModalRespostasProntas, hideModalRespostasProntas] = useModal(
    ({ in: open }) => (
      <ModalRespostasProntas
        open={open}
        onClose={hideModalRespostasProntas}
        onSelect={handleSelectRespostaPronta}
      />
    ),
  );

  const [showModalListaContatos, hideModalListaContatos] = useModal(
    ({ in: open }) => (
      <ModalListaContatos
        open={open}
        onClose={hideModalListaContatos}
        onSelect={(contato: ContatoInterface) => {
          if (onSendContato) onSendContato(contato);
        }}
        filterByType={TipoCanalEnum.WHATSAPP}
      />
    ),
    [replyMessage?.id],
  );

  const [showModalGaleria, hideModalGaleria] = useModal(({ in: open }) => (
    <ModalGaleria
      open={open}
      onClose={hideModalGaleria}
      onSendFiles={(storageIds: string[]) => {
        if (onSendFromGaleria) onSendFromGaleria(storageIds);
      }}
      isInstagramOrMessengerChannel={isInstagramOrMessengerChannel}
    />
  ));

  const [showModalEnviarMensagemCTA, hideModalEnviarMensagemCTA] = useModal(
    ({ in: open }) => (
      <ModalEnviarMensagemCTA
        open={open}
        onClose={hideModalEnviarMensagemCTA}
        onSendInteractiveMessage={onSendCTA}
      />
    ),
  );

  return (
    <Paper className={classes.root} elevation={0}>
      {replyMessage && (
        <ReplyMessagePreview
          replyMessage={replyMessage}
          isGroup
          atendimentoSelecionado={atendimentoSelecionado}
          handleClearReplyMessage={handleClearReplyMessage}
        />
      )}

      <Paper className={classes.rootInput} elevation={0}>
        {isEncaminhandoMensagens ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexGrow={1}
          >
            <Box
              style={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <IconButton onClick={handleCancelEncaminharMensagens}>
                <CloseOutlined />
              </IconButton>{' '}
              {totalMensagensParaEncaminhar > 0 ? (
                <>
                  {totalMensagensParaEncaminhar}
                  {totalMensagensParaEncaminhar === 1
                    ? ' mensagem selecionada'
                    : ' mensagens selecionadas'}
                </>
              ) : (
                'Nenhuma mensagem selecionada'
              )}
            </Box>
            <ButtonEncaminharMensagens />
          </Box>
        ) : (
          <>
            <EmojiButton onSelect={handleSelectEmoji} />

            <Box>
              <ButtonBase
                disableRipple
                disabled={loading}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                  setAnchorEl(e.currentTarget)
                }
              >
                <AddIcon />
              </ButtonBase>

              <Menu
                elevation={0}
                id="mentions-input-menu"
                anchorEl={anchorEl}
                classes={{ paper: classes.paper }}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <Box className={classes.menuContent}>
                  {isOfficialWhatsAppChannel &&
                    atendimentoSelecionado?.sessaoStatus &&
                    onSendCTA && (
                      <ActionButton
                        handleClose={() => setAnchorEl(null)}
                        bgColor={colors.light.primary.main}
                        tooltip="Mensagem CTA URL"
                        onClick={showModalEnviarMensagemCTA}
                      >
                        <InteractiveIcon />
                      </ActionButton>
                    )}

                  {onSendContato && !isInstagramOrMessengerChannel && (
                    <ActionButton
                      handleClose={() => setAnchorEl(null)}
                      tooltip="Contato"
                      bgColor={blue[500]}
                      onClick={showModalListaContatos}
                    >
                      <PersonIcon />
                    </ActionButton>
                  )}

                  {onSendFromGaleria && (
                    <ActionButton
                      handleClose={() => setAnchorEl(null)}
                      bgColor={purple[500]}
                      tooltip="Arquivos da galeria"
                      onClick={showModalGaleria}
                    >
                      <PhotoFilterIcon />
                    </ActionButton>
                  )}

                  {onSelectFiles && (
                    <ActionButton
                      htmlFor="mentions-input-send-image"
                      handleClose={() => setAnchorEl(null)}
                      bgColor={deepOrange[500]}
                      tooltip="Imagens"
                    >
                      <PhotoIcon />
                      <HiddenFileInput
                        id="mentions-input-send-image"
                        accept="image/jpeg, image/png"
                        multiple
                        onFiles={onSelectFiles}
                      />
                    </ActionButton>
                  )}

                  {onSelectFiles && !isInstagramOrMessengerChannel && (
                    <ActionButton
                      htmlFor="mentions-input-send-document"
                      handleClose={() => setAnchorEl(null)}
                      bgColor={deepPurple[500]}
                      tooltip="Documento"
                    >
                      <InsertDriveFileIcon />
                      <HiddenFileInput
                        id="mentions-input-send-document"
                        accept="*"
                        multiple
                        onFiles={onSelectFiles}
                      />
                    </ActionButton>
                  )}
                </Box>
              </Menu>
            </Box>

            <Box
              className={classes.inputContainer}
              onClick={() => chatInputRef.current?.focus()}
            >
              <Tooltip title="Respostas prontas" placement="top">
                <ButtonBase
                  disabled={loading}
                  onClick={showModalRespostasProntas}
                  disableRipple
                >
                  <FormatQuoteIcon color="inherit" />
                </ButtonBase>
              </Tooltip>

              <ChatInput
                id="input-send-message"
                ref={chatInputRef}
                fetchUsers={fetchUsers}
                onSend={handleSendInputText}
                onInputChange={(text, hasCnt, mentions) => {
                  setHasContent(hasCnt);
                  setInputValue(text);
                  setInputMentions(mentions || []);
                }}
                showModalRespostasProntas={showModalRespostasProntas}
                disabled={loading}
                placeholder="Digite @ para mencionar um participante"
                autoFocus
                initialValue={getInitialValue()}
              />

              <Zoom in={hasContent}>
                <ButtonBase
                  disabled={loading}
                  onClick={handleClearEditor}
                  disableRipple
                >
                  <ClearIcon />
                </ButtonBase>
              </Zoom>
            </Box>

            {hasContent ? (
              <ButtonBase
                id="mentions-input-send-btn"
                onClick={() => {
                  chatInputRef.current?.send();
                }}
                disableRipple
              >
                {loading ? (
                  <CircularProgress style={{ width: 24, height: 24 }} />
                ) : (
                  <SendIcon color="primary" />
                )}
              </ButtonBase>
            ) : (
              <AudioRecorder
                onEnded={(blob, dur) => {
                  if (onSendAudio) onSendAudio(blob, dur);
                }}
              />
            )}
          </>
        )}
      </Paper>
    </Paper>
  );
}
